'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMicroSource, type MicroContentItem } from '@akademi/site-builder-runtime';
import type { RendererProps } from '../types';
import { KindBadge, PopupHeader, padded } from '../chrome';

/**
 * Learning-path player — legacy-parity flow: each step opens its
 * referenced item's renderer INSIDE the popup (via
 * {@code onOpenStep}); the popup shows a back-to-path pill and
 * auto-returns on step completion. Step completion persists on the
 * STEP item's progress row, so the path recomputes statuses from
 * {@code progress.fetchMine} on every re-entry.
 *
 * <p>Body matches the admin's {@code LearningPathBody} editor:
 * {@code {steps:[{contentId, labelOverride?, descriptionOverride?,
 * optional?}], sequenced? (default true), intro?, completionLabel?}}.
 *
 * <p>Whole-path completion (all REQUIRED steps done — optional steps
 * never block, unavailable required steps DO block) fires
 * {@code onComplete} exactly once.
 */

interface PathStep {
  contentId: number | string;
  labelOverride?: string;
  descriptionOverride?: string;
  optional?: boolean;
}

type StepStatus = 'completed' | 'in_progress' | 'available' | 'locked' | 'unavailable';

const TEAL = '#0d9488';
const MUTED = 'var(--color-text-secondary, #64748b)';

export function LearningPathRenderer({ item, onComplete, onOpenStep }: RendererProps) {
  const micro = useMicroSource();
  const { t } = useTranslation('site-builder-blocks');
  const body =
    (item.body as {
      steps?: PathStep[];
      sequenced?: boolean;
      intro?: string | null;
      completionLabel?: string | null;
    }) ?? {};
  const steps = useMemo(() => (Array.isArray(body.steps) ? body.steps : []), [body.steps]);
  const sequenced = body.sequenced !== false; // default true
  const intro = body.intro ?? null;
  const completionLabel = body.completionLabel ?? t('microLearningPath.completionDefault');

  const [resolved, setResolved] = useState<Map<string, MicroContentItem> | null>(null);
  const [completedIds, setCompletedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [inProgressIds, setInProgressIds] = useState<ReadonlySet<string>>(() => new Set());
  const [error, setError] = useState<string | null>(null);
  const completeFiredRef = useRef(false);

  // Step metadata (content.fetchById per step) + the learner's progress
  // rows, in parallel. Runs once per mount — returning from a step
  // remounts this renderer, which re-reads fresh progress.
  useEffect(() => {
    if (!micro) {
      setResolved(new Map());
      return;
    }
    let cancelled = false;
    const progressNs = micro.progress;
    const load = async () => {
      const [items, rows] = await Promise.all([
        Promise.all(steps.map((s) => micro.content.fetchById(s.contentId).catch(() => null))),
        progressNs?.fetchMine ? progressNs.fetchMine().catch(() => []) : Promise.resolve([]),
      ]);
      if (cancelled) return;
      const map = new Map<string, MicroContentItem>();
      items.forEach((it) => {
        if (it) map.set(String(it.id), it);
      });
      setResolved(map);
      setCompletedIds(
        new Set(rows.filter((r) => r.status === 'completed').map((r) => String(r.itemId))),
      );
      setInProgressIds(
        new Set(rows.filter((r) => r.status === 'in-progress').map((r) => String(r.itemId))),
      );
    };
    void load().catch((e: unknown) => {
      if (!cancelled) setError(e instanceof Error ? e.message : String(e));
    });
    return () => {
      cancelled = true;
    };
  }, [micro, steps]);

  // Per-step status, ported from legacy: sequenced paths unlock a step
  // only once all PRIOR REQUIRED steps are completed; skipped optional
  // steps never block; unavailable REQUIRED steps close the gate.
  const statuses: StepStatus[] = useMemo(() => {
    if (resolved == null) return steps.map(() => 'locked');
    const out: StepStatus[] = [];
    let priorRequiredAllDone = true;
    for (const s of steps) {
      const key = String(s.contentId);
      if (!resolved.has(key)) {
        out.push('unavailable');
        if (!s.optional) priorRequiredAllDone = false;
        continue;
      }
      if (completedIds.has(key)) {
        out.push('completed');
        continue;
      }
      if (!sequenced || priorRequiredAllDone) {
        out.push(inProgressIds.has(key) ? 'in_progress' : 'available');
      } else {
        out.push('locked');
      }
      if (!s.optional) priorRequiredAllDone = false;
    }
    return out;
  }, [resolved, completedIds, inProgressIds, sequenced, steps]);

  const allRequiredDone = useMemo(() => {
    if (resolved == null || steps.length === 0) return false;
    return steps.every((s, i) => {
      if (s.optional) return true;
      if (statuses[i] === 'unavailable') return false;
      return completedIds.has(String(s.contentId));
    });
  }, [steps, statuses, completedIds, resolved]);

  // Fire onComplete exactly once when every required step is done.
  useEffect(() => {
    if (allRequiredDone && !completeFiredRef.current) {
      completeFiredRef.current = true;
      onComplete();
    }
  }, [allRequiredDone, onComplete]);

  const requiredCount = steps.filter((s) => !s.optional).length;
  const requiredDoneCount = steps.filter(
    (s, i) => !s.optional && statuses[i] !== 'unavailable' && completedIds.has(String(s.contentId)),
  ).length;
  const progressPercent =
    requiredCount === 0 ? 0 : Math.round((requiredDoneCount / requiredCount) * 100);

  return (
    <>
      <PopupHeader kind={item.kind} title={item.title} description={item.description} />
      <div style={{ ...padded, paddingTop: 0 }}>
        {/* Progress bar — required steps only. */}
        <div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 6,
            }}
          >
            <span
              style={{
                fontSize: '11px',
                color: MUTED,
                fontWeight: 600,
                letterSpacing: 'var(--font-tracking-wider, 0.05em)',
                textTransform: 'uppercase',
              }}
            >
              {t('microLearningPath.progress')}
            </span>
            <span style={{ fontSize: '13px', fontWeight: 600 }}>
              {requiredDoneCount} / {requiredCount} {t('microLearningPath.stepsUnit')}
            </span>
          </div>
          <div
            style={{
              height: 8,
              background: 'rgba(13,148,136,0.12)',
              borderRadius: 999,
              overflow: 'hidden',
            }}
          >
            <div
              role="progressbar"
              aria-valuenow={progressPercent}
              aria-valuemin={0}
              aria-valuemax={100}
              style={{
                width: `${progressPercent}%`,
                height: '100%',
                background: TEAL,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>

        {intro && (
          <p
            style={{
              marginTop: 16,
              marginBottom: 0,
              fontSize: 'var(--font-size-sm, 14px)',
              lineHeight: 1.6,
              padding: '10px 14px',
              background: 'rgba(13,148,136,0.06)',
              borderLeft: `3px solid ${TEAL}`,
              borderRadius: '0 6px 6px 0',
            }}
          >
            {intro}
          </p>
        )}

        {error && (
          <p
            style={{
              marginTop: 16,
              fontSize: 'var(--font-size-sm, 14px)',
              color: 'var(--color-danger, #dc2626)',
            }}
          >
            {t('microLearningPath.loadFailed')}
            {error}
          </p>
        )}
        {resolved == null && !error && (
          <p style={{ marginTop: 20, fontSize: 'var(--font-size-sm, 14px)', color: MUTED }}>
            {t('microLearningPath.loading')}
          </p>
        )}
        {resolved != null && steps.length === 0 && (
          <p style={{ marginTop: 20, fontSize: 'var(--font-size-sm, 14px)', color: MUTED }}>
            {t('microLearningPath.empty')}
          </p>
        )}

        {resolved != null && steps.length > 0 && (
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {steps.map((s, i) => (
              <PathStepCard
                key={i}
                index={i}
                step={s}
                stepItem={resolved.get(String(s.contentId))}
                status={statuses[i] ?? 'locked'}
                onOpen={onOpenStep ? () => onOpenStep(s.contentId) : undefined}
                connectorBelow={i < steps.length - 1}
              />
            ))}
          </div>
        )}

        {allRequiredDone && (
          <div
            style={{
              marginTop: 24,
              padding: '18px 22px',
              background: 'linear-gradient(135deg, rgba(13,148,136,0.10), rgba(20,184,166,0.05))',
              border: '1px solid rgba(13,148,136,0.3)',
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 14,
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: TEAL,
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: 'var(--font-size-base, 15px)', fontWeight: 600 }}>
                {completionLabel}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '13px', color: MUTED }}>
                {t('microLearningPath.allStepsDone', { count: requiredCount })}
                {steps.some((s, i) => s.optional && statuses[i] !== 'completed') &&
                  t('microLearningPath.optionalHint')}
                .
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/** One step row: status badge, kind chip + title + meta, status CTA. */
function PathStepCard({
  index,
  step,
  stepItem,
  status,
  onOpen,
  connectorBelow,
}: {
  index: number;
  step: PathStep;
  stepItem: MicroContentItem | undefined;
  status: StepStatus;
  onOpen: (() => void) | undefined;
  connectorBelow: boolean;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const styling = (() => {
    switch (status) {
      case 'completed':
        return {
          bg: 'rgba(13,148,136,0.06)',
          border: 'rgba(13,148,136,0.3)',
          opacity: 0.85,
          interactive: false,
        };
      case 'in_progress':
        return {
          bg: 'rgba(249,115,22,0.05)',
          border: 'rgba(249,115,22,0.4)',
          opacity: 1,
          interactive: true,
        };
      case 'available':
        return {
          bg: 'var(--color-surface-card, #fff)',
          border: 'rgba(13,148,136,0.5)',
          opacity: 1,
          interactive: true,
        };
      case 'locked':
        return {
          bg: 'var(--color-surface-page, #f4f4f5)',
          border: 'rgba(148,163,184,0.4)',
          opacity: 0.6,
          interactive: false,
        };
      default:
        return {
          bg: 'rgba(252,165,165,0.06)',
          border: 'rgba(220,38,38,0.3)',
          opacity: 0.7,
          interactive: false,
        };
    }
  })();
  // Without a popup-provided step opener there is nothing to navigate to.
  const interactive = styling.interactive && onOpen != null;

  const title =
    step.labelOverride ||
    stepItem?.title ||
    t('microLearningPath.stepFallback', { index: index + 1 });
  const description = step.descriptionOverride ?? stepItem?.description ?? null;

  const metaBits: string[] = [];
  if (stepItem?.meta?.duration) metaBits.push(stepItem.meta.duration);
  if (stepItem?.meta?.readingMinutes)
    metaBits.push(t('microCard.meta.readingMinutes', { count: stepItem.meta.readingMinutes }));
  if (stepItem?.meta?.cardCount)
    metaBits.push(t('microCard.meta.cards', { count: stepItem.meta.cardCount }));
  if (stepItem?.meta?.questionCount)
    metaBits.push(t('microCard.meta.questions', { count: stepItem.meta.questionCount }));

  return (
    <div style={{ position: 'relative' }}>
      <div
        onClick={interactive ? onOpen : undefined}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onKeyDown={
          interactive
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen?.();
                }
              }
            : undefined
        }
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          padding: '14px 16px',
          background: styling.bg,
          border: `1px solid ${styling.border}`,
          borderRadius: 12,
          opacity: styling.opacity,
          cursor: interactive ? 'pointer' : 'default',
        }}
      >
        <StepStatusBadge status={status} index={index} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            {stepItem && <KindBadge kind={stepItem.kind} />}
            {step.optional && (
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: 'var(--font-tracking-wider, 0.06em)',
                  textTransform: 'uppercase',
                  color: MUTED,
                  background: 'rgba(100,116,139,0.12)',
                  padding: '2px 6px',
                  borderRadius: 3,
                }}
              >
                {t('microLearningPath.optional')}
              </span>
            )}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={title}
          >
            {title}
          </p>
          {status === 'unavailable' ? (
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#92400e' }}>
              {t('microLearningPath.unavailable')}
            </p>
          ) : (
            description && (
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: '13px',
                  color: MUTED,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical' as const,
                }}
              >
                {description}
              </p>
            )
          )}
          {metaBits.length > 0 && (
            <p
              style={{
                margin: '6px 0 0',
                fontSize: '11px',
                color: 'var(--color-text-tertiary, #94a3b8)',
                fontWeight: 500,
              }}
            >
              {metaBits.join(' · ')}
            </p>
          )}
        </div>

        {interactive && (
          <div
            style={{
              flexShrink: 0,
              color: status === 'in_progress' ? 'var(--color-warning, #f97316)' : TEAL,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: '13px',
              fontWeight: 600,
              alignSelf: 'center',
            }}
          >
            {status === 'in_progress'
              ? t('microLearningPath.continue')
              : t('microLearningPath.start')}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </div>
        )}
      </div>

      {connectorBelow && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: 33,
            top: '100%',
            width: 2,
            height: 12,
            background: status === 'completed' ? TEAL : 'rgba(148,163,184,0.4)',
          }}
        />
      )}
    </div>
  );
}

function StepStatusBadge({ status, index }: { status: StepStatus; index: number }) {
  const common = {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    fontSize: '13px',
    fontWeight: 700,
  } as const;
  switch (status) {
    case 'completed':
      return (
        <div style={{ ...common, background: TEAL, color: '#fff' }}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
      );
    case 'in_progress':
      return (
        <div style={{ ...common, background: '#fb923c', color: '#fff' }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M12 4v8l4 2" />
            <circle cx="12" cy="12" r="9" />
          </svg>
        </div>
      );
    case 'locked':
      return (
        <div style={{ ...common, background: 'rgba(148,163,184,0.2)', color: MUTED }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <rect x="5" y="11" width="14" height="9" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
        </div>
      );
    case 'unavailable':
      return (
        <div
          style={{
            ...common,
            background: 'rgba(220,38,38,0.12)',
            color: 'var(--color-danger, #dc2626)',
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </div>
      );
    default:
      return (
        <div style={{ ...common, background: 'rgba(13,148,136,0.15)', color: TEAL }}>
          {index + 1}
        </div>
      );
  }
}
